import { Trophy, Target, Flame, Clock, TrendingUp, TrendingDown, Star, Lock, Brain, Type, Images } from 'lucide-react';
import { Card } from '@/components/common';
import { FlagImage } from '@/components/flag';
import { useStatsStore, useAchievementsStore, type QuizLength } from '@/store';
import { formatDuration, getRelativeTimeString } from '@/lib/utils';
import countries from '@/data/countries.json';
import type { Country, QuizMode } from '@/types';

const allCountries = countries as Country[];
const TOTAL_COUNTRIES = allCountries.length;
const QUIZ_LENGTHS: QuizLength[] = [20, 50, 100, 'all'];

const quizModes: { mode: QuizMode; icon: typeof Brain; title: string }[] = [
  { mode: 'flag-to-name-mc', icon: Brain, title: 'Find the Country' },
  { mode: 'flag-to-name-type', icon: Type, title: 'Name the Country' },
  { mode: 'name-to-flag-mc', icon: Images, title: 'Find the Flag' },
];

function getLengthLabel(length: QuizLength): string {
  return length === 'all' ? `All ${TOTAL_COUNTRIES}` : String(length);
}

export function StatsPage() {
  const { overall, sessions, getBestFlags, getWorstFlags } = useStatsStore();
  const { hasPerfectScore, isLengthUnlocked } = useAchievementsStore();

  const accuracy = overall.totalQuestions > 0
    ? Math.round((overall.correctAnswers / overall.totalQuestions) * 100)
    : 0;

  const bestFlags = getBestFlags(5);
  const worstFlags = getWorstFlags(5);

  const getCountryName = (code: string) =>
    allCountries.find((c) => c.code === code)?.name ?? code;

  const achievementsSection = (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Star className="w-5 h-5 text-amber-500" />
        <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">Achievements</h2>
      </div>
      <div className="space-y-3">
        {quizModes.map(({ mode, icon: Icon, title }) => (
          <Card key={mode} className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="font-medium text-stone-900 dark:text-stone-100">{title}</h3>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {QUIZ_LENGTHS.map((length) => {
                const unlocked = isLengthUnlocked(mode, length);
                const hasPerfect = hasPerfectScore(mode, length);

                return (
                  <div
                    key={length}
                    className={`
                      flex flex-col items-center justify-center p-2 rounded-lg border text-center
                      ${hasPerfect
                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
                        : unlocked
                          ? 'bg-stone-50 dark:bg-stone-800 border-stone-200 dark:border-stone-700'
                          : 'bg-stone-100 dark:bg-stone-900 border-stone-200 dark:border-stone-800 opacity-50'
                      }
                    `}
                  >
                    {hasPerfect ? (
                      <Star className="w-4 h-4 text-amber-500 mb-1" />
                    ) : unlocked ? (
                      <div className="w-4 h-4 rounded-full border-2 border-stone-300 dark:border-stone-600 mb-1" />
                    ) : (
                      <Lock className="w-4 h-4 text-stone-400 mb-1" />
                    )}
                    <span className={`text-xs font-medium ${hasPerfect ? 'text-amber-700 dark:text-amber-300' : 'text-stone-600 dark:text-stone-400'}`}>
                      {getLengthLabel(length)}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  if (overall.totalQuestions === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-stone-900 dark:text-stone-100">Statistics</h1>
        <Card className="text-center py-12">
          <div className="text-5xl mb-4">📊</div>
          <h2 className="text-xl font-semibold text-stone-900 dark:text-stone-100 mb-2">No stats yet</h2>
          <p className="text-stone-500">
            Complete some quizzes to see your progress!
          </p>
        </Card>
        {achievementsSection}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Statistics</h1>

      {/* Overview */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success-100 dark:bg-success-950 rounded-lg">
              <Target className="w-5 h-5 text-success-600 dark:text-success-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{accuracy}%</div>
              <div className="text-sm text-stone-500">Accuracy</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-warning-100 dark:bg-warning-950 rounded-lg">
              <Flame className="w-5 h-5 text-warning-600 dark:text-warning-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{overall.currentStreak}</div>
              <div className="text-sm text-stone-500">Day streak</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-950 rounded-lg">
              <Trophy className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{overall.totalQuizzes}</div>
              <div className="text-sm text-stone-500">Quizzes</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent-purple/20 rounded-lg">
              <Clock className="w-5 h-5 text-accent-purple" />
            </div>
            <div>
              <div className="text-2xl font-bold">
                {formatDuration(overall.totalPracticeTime)}
              </div>
              <div className="text-sm text-stone-500">Total time</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Questions answered */}
      <Card>
        <div className="text-center">
          <div className="text-4xl font-bold text-primary-600 dark:text-primary-400">
            {overall.totalQuestions}
          </div>
          <div className="text-stone-500">Questions answered</div>
          <div className="text-sm text-success-600 dark:text-success-400 mt-1">
            {overall.correctAnswers} correct
          </div>
        </div>
      </Card>

      {achievementsSection}

      {/* Best flags */}
      {bestFlags.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-success-500" />
            <h2 className="text-lg font-semibold">Best Flags</h2>
          </div>
          <Card className="divide-y divide-stone-100 dark:divide-stone-800">
            {bestFlags.map((stat) => (
              <div
                key={stat.countryCode}
                className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
              >
                <FlagImage countryCode={stat.countryCode} size="sm" />
                <span className="flex-1 font-medium">
                  {getCountryName(stat.countryCode)}
                </span>
                <span className="text-success-600 dark:text-success-400 font-semibold">
                  {Math.round((stat.timesCorrect / stat.timesShown) * 100)}%
                </span>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* Worst flags */}
      {worstFlags.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-error-500" />
            <h2 className="text-lg font-semibold">Need Practice</h2>
          </div>
          <Card className="divide-y divide-stone-100 dark:divide-stone-800">
            {worstFlags.map((stat) => (
              <div
                key={stat.countryCode}
                className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
              >
                <FlagImage countryCode={stat.countryCode} size="sm" />
                <span className="flex-1 font-medium">
                  {getCountryName(stat.countryCode)}
                </span>
                <span className="text-error-600 dark:text-error-400 font-semibold">
                  {Math.round((stat.timesCorrect / stat.timesShown) * 100)}%
                </span>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* Recent sessions */}
      {sessions.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Recent Sessions</h2>
          <div className="space-y-2">
            {sessions.slice(0, 5).map((session) => (
              <Card key={session.id} className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-medium">
                      {session.correctCount}/{session.questionsCount} correct
                    </div>
                    <div className="text-sm text-stone-500">
                      {getRelativeTimeString(session.date)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold">
                      {Math.round((session.correctCount / session.questionsCount) * 100)}%
                    </div>
                    <div className="text-sm text-stone-500">
                      {formatDuration(session.duration)}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
